import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Tabs,
  Tab,
  Link,
} from '@mui/material';
import {
  ExpandMore,
  Send,
  HelpOutline,
  Article,
  Email,
  Search,
  CheckCircle,
} from '@mui/icons-material';
import { useSnackbar } from 'src/view/components/SnackbarService';
import clientService from 'src/clientService';
import { SupportTicketCreate, TicketCategory, TicketPriority } from 'src/api/Api';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const FAQ_DATA: FAQItem[] = [
  {
    category: 'General',
    question: 'What is Darukaa.Earth?',
    answer:
      'Darukaa.Earth is a cloud-native biodiversity and climate-risk platform for monitoring, analysis, carbon & biodiversity credit calculation, and stakeholder workflows. It combines remote sensing, in-situ sensors (acoustic & camera),AI/ML Models and reporting tools.',
  },
  {
    category: 'General',
    question: 'Who should use the platform?',
    answer:
      'Project developers, conservation/NGO teams, verifiers, researchers, and enterprises doing nature-based projects: carbon, biodiversity, restoration, or climate risk assessment.',
  },
  {
    category: 'Accounts & Authentication',
    question: 'How do I create an account?',
    answer:
      'Sign up via the web UI or use the API endpoint POST /api/v1/auth/signup. Verify your email to complete registration.',
  },
  {
    category: 'Accounts & Authentication',
    question: 'How do I authenticate with the API?',
    answer:
      'Obtain an access token by calling POST /auth/login with email/password. Send Authorization: Bearer <access_token> in API requests.',
  },
  {
    category: 'Accounts & Authentication',
    question: 'How long are tokens valid and how do I refresh them?',
    answer:
      'Access tokens are short-lived; use POST /auth/refresh with your refresh token to get a new access token. Token lifetimes are configured on the server.',
  },
  {
    category: 'Data & Uploads',
    question: 'What formats are supported for geodata and media?',
    answer:
      'Vectors: GeoJSON (RFC 7946), shapefile zip. Rasters: GeoTIFF. Audio: WAV (16-bit, ≥44.1 kHz preferred), MP3, M4A, WEBM. Images: JPEG/PNG with EXIF.',
  },
  {
    category: 'Data & Uploads',
    question: 'What are file size and upload limits?',
    answer:
      'Single upload max: 500 MB for general uploads. Audio upload: 25 MB per file, max 20 files per request, 300 seconds max duration.',
  },
  {
    category: 'Troubleshooting',
    question: 'I received 429 Too Many Requests — what now?',
    answer:
      'Back off and retry after Retry-After header. Implement exponential backoff and respect rate-limit headers (X-RateLimit-Limit, X-RateLimit-Remaining, X-RateLimit-Reset).',
  },
  {
    category: 'Troubleshooting',
    question: 'My file upload returns 413 Payload Too Large — what should I do?',
    answer:
      'Split the upload into smaller chunks if supported, compress/convert files, or use the platform\'s batch upload API with signed URLs. Contact support if you need higher limits.',
  },
  {
    category: 'Troubleshooting',
    question: 'I can\'t find a project or site — 404 — what to check?',
    answer:
      'Verify you\'re using the correct project_id/site_id, that the account has access, and that the get_satellite_images flag (if used) is correct. Provide request_id and a timestamp when contacting support.',
  },
];

const HelpSupportPage: React.FC = () => {
  const { showSnackbar, snackbarType } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketSubmitted, setTicketSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    category: 'General',
    priority: 'Medium',
  });
  const [errors, setErrors] = useState({
    subject: '',
    message: '',
  });

  const categories = ['General', 'Technical', 'Billing', 'Feature Request', 'Bug Report', 'Account'];
  const priorities = ['Low', 'Medium', 'High', 'Urgent'];

  const filteredFAQs = FAQ_DATA.filter((faq) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      faq.question.toLowerCase().includes(query) ||
      faq.answer.toLowerCase().includes(query) ||
      faq.category.toLowerCase().includes(query)
    );
  });

  const groupedFAQs = filteredFAQs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {} as Record<string, FAQItem[]>);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;
    const newErrors = { subject: '', message: '' };

    if (!formData.subject.trim() || formData.subject.trim().length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters.';
      isValid = false;
    }

    if (!formData.message.trim() || formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showSnackbar({
        message: 'Please fix the errors in the form.',
        type: snackbarType.ERROR,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const requestBody: SupportTicketCreate = {
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        category: formData.category as TicketCategory,
        priority: formData.priority as TicketPriority,
      };
      
      await clientService.api.createSupportTicketApiV1SupportTicketsPost(requestBody);

      setTicketSubmitted(true);
      setFormData({
        subject: '',
        message: '',
        category: 'General',
        priority: 'Medium',
      });
      showSnackbar({
        message: 'Support ticket submitted successfully! We\'ll get back to you soon.',
        type: snackbarType.SUCCESS,
      });
    } catch (error: any) {
      console.error('Error submitting support ticket:', error);
      const errorMessage = 'Failed to submit support ticket. Please try again.';
      showSnackbar({
        message: errorMessage,
        type: snackbarType.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6, backgroundColor: 'transparent', minHeight: '100vh' }}>
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom 
          sx={{ 
            fontWeight: 700, 
            color: '#10B981',
            letterSpacing: '-0.02em',
            mb: 1
          }}>
          Help & Support
        </Typography>
        <Typography variant="h6" sx={{ color: '#999', fontWeight: 400 }}>
          Find answers to common questions or contact our support team
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => {
          setActiveTab(newValue);
          setTicketSubmitted(false);
        }}
        sx={{
          mb: 5,
          borderBottom: '1px solid #1A1A1A',
          '& .MuiTab-root': {
            color: '#666',
            fontSize: '15px',
            fontWeight: 500,
            textTransform: 'none',
            minHeight: '48px',
            pb: 2,
            transition: 'all 0.2s',
            '&:hover': {
              color: '#FFF',
            },
            '&.Mui-selected': {
              color: '#10B981',
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#10B981',
            height: '2px',
          },
        }}
      >
        <Tab label="FAQs" icon={<HelpOutline />} iconPosition="start" />
        <Tab label="Contact Support" icon={<Send />} iconPosition="start" />
        <Tab label="Documentation" icon={<Article />} iconPosition="start" />
      </Tabs>

      {/* FAQs Tab */}
      {activeTab === 0 && (
        <Box>
          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1.5, color: '#10B981' }} />,
                sx: { 
                  color: '#FFF',
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1A1A1A',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#333',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#10B981',
                    borderWidth: '1px',
                  },
                }
              }}
            />
            {searchQuery && (
              <Typography variant="body2" sx={{ mt: 2, ml: 1, color: '#10B981', fontWeight: 500 }}>
                Found {filteredFAQs.length} result{filteredFAQs.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          {Object.keys(groupedFAQs).length === 0 ? (
            <Paper sx={{ 
              p: 6, 
              backgroundColor: 'rgba(255, 255, 255, 0.02)', 
              borderRadius: '16px',
              border: '1px solid #1A1A1A',
              textAlign: 'center' 
            }}>
              <Typography variant="h6" sx={{ color: '#666' }}>
                No FAQs found matching your search.
              </Typography>
            </Paper>
          ) : (
            Object.entries(groupedFAQs).map(([category, faqs]) => (
              <Box key={category} sx={{ mb: 5 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#10B981', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: '4px', height: '20px', bgcolor: '#10B981', borderRadius: '2px' }} />
                  {category}
                </Typography>
                {faqs.map((faq, index) => (
                  <Accordion
                    key={index}
                    sx={{
                      mb: 1.5,
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      backgroundImage: 'none',
                      color: '#FFF',
                      borderRadius: '12px !important',
                      border: '1px solid #1A1A1A',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        border: '1px solid #10B981',
                        backgroundColor: 'rgba(16, 185, 129, 0.03)',
                      },
                      '&:hover': {
                        borderColor: '#333',
                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: '#10B981' }} />}
                      sx={{
                        px: 3,
                        py: 0.5,
                        '& .MuiAccordionSummary-content.Mui-expanded': {
                          mb: 0
                        }
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                      <Typography sx={{ color: '#999', lineHeight: 1.6, fontSize: '14px' }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ))
          )}
        </Box>
      )}

      {/* Contact Support Tab */}
      {activeTab === 1 && (
        <Paper sx={{ 
          p: 5, 
          backgroundColor: 'rgba(255, 255, 255, 0.02)', 
          borderRadius: '24px',
          border: '1px solid #1A1A1A',
          backgroundImage: 'none'
        }}>
          {ticketSubmitted ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle sx={{ fontSize: 64, color: '#10B981', mb: 3 }} />
              <Typography variant="h4" sx={{ color: '#FFF', fontWeight: 700, mb: 2 }}>
                Ticket Submitted!
              </Typography>
              <Typography variant="body1" sx={{ color: '#999', mb: 4, maxWidth: '500px', mx: 'auto' }}>
                We've received your support request and will get back to you soon. You'll receive an email confirmation shortly.
              </Typography>
              <Button 
                variant="outlined" 
                onClick={() => setTicketSubmitted(false)}
                sx={{ 
                  color: '#10B981', 
                  borderColor: '#10B981',
                  borderRadius: '10px',
                  px: 4,
                  '&:hover': { borderColor: '#FFF', color: '#FFF' }
                }}
              >
                Send Another Message
              </Button>
            </Box>
          ) : (
            <>
              <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, color: '#FFF' }}>
                Submit a Support Ticket
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    error={!!errors.subject}
                    helperText={errors.subject}
                    disabled={isSubmitting}
                    InputProps={{
                      sx: { color: '#FFF', backgroundColor: 'rgba(255, 255, 255, 0.01)', borderRadius: '10px' }
                    }}
                    InputLabelProps={{ sx: { color: '#666', '&.Mui-focused': { color: '#10B981' } } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#1A1A1A' },
                        '&:hover fieldset': { borderColor: '#333' },
                        '&.Mui-focused fieldset': { borderColor: '#10B981' },
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    SelectProps={{ native: true }}
                    disabled={isSubmitting}
                    InputProps={{
                      sx: { color: '#FFF', backgroundColor: 'rgba(255, 255, 255, 0.01)', borderRadius: '10px' }
                    }}
                    InputLabelProps={{ sx: { color: '#666', '&.Mui-focused': { color: '#10B981' } } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#1A1A1A' },
                        '&:hover fieldset': { borderColor: '#333' },
                        '&.Mui-focused fieldset': { borderColor: '#10B981' },
                      },
                      '& select option': { backgroundColor: '#111', color: '#FFF' }
                    }}
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    SelectProps={{ native: true }}
                    disabled={isSubmitting}
                    InputProps={{
                      sx: { color: '#FFF', backgroundColor: 'rgba(255, 255, 255, 0.01)', borderRadius: '10px' }
                    }}
                    InputLabelProps={{ sx: { color: '#666', '&.Mui-focused': { color: '#10B981' } } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#1A1A1A' },
                        '&:hover fieldset': { borderColor: '#333' },
                        '&.Mui-focused fieldset': { borderColor: '#10B981' },
                      },
                      '& select option': { backgroundColor: '#111', color: '#FFF' }
                    }}
                  >
                    {priorities.map((pri) => (
                      <option key={pri} value={pri}>
                        {pri}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    error={!!errors.message}
                    helperText={errors.message}
                    disabled={isSubmitting}
                    InputProps={{
                      sx: { color: '#FFF', backgroundColor: 'rgba(255, 255, 255, 0.01)', borderRadius: '10px' }
                    }}
                    InputLabelProps={{ sx: { color: '#666', '&.Mui-focused': { color: '#10B981' } } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#1A1A1A' },
                        '&:hover fieldset': { borderColor: '#333' },
                        '&.Mui-focused fieldset': { borderColor: '#10B981' },
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                      sx={{
                        backgroundColor: '#10B981',
                        color: '#000',
                        fontWeight: 700,
                        px: 4,
                        py: 1.5,
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontSize: '15px',
                        '&:hover': {
                          backgroundColor: '#FFF',
                          color: '#000',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(16, 185, 129, 0.25)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Support Ticket'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </Paper>
      )}

      {/* Documentation Tab */}
      {activeTab === 2 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ 
              p: 5, 
              height: '100%', 
              backgroundColor: 'rgba(255, 255, 255, 0.02)', 
              borderRadius: '24px',
              border: '1px solid #1A1A1A',
              backgroundImage: 'none',
              transition: 'all 0.3s',
              '&:hover': { borderColor: '#10B981', backgroundColor: 'rgba(255, 255, 255, 0.03)' }
            }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#FFF', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Article sx={{ color: '#10B981' }} />
                Documentation
              </Typography>
              <Typography variant="body1" sx={{ color: '#999', mb: 4, lineHeight: 1.7 }}>
                Access comprehensive documentation, interactive guides, technical specs, and complete API references to get the most out of Darukaa.
              </Typography>
              <Button
                variant="outlined"
                href="https://docs.darukaa.com/api/overview/"
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: '10px',
                  borderColor: '#10B981',
                  color: '#10B981',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#FFF',
                    backgroundColor: 'rgba(16, 185, 129, 0.05)',
                    color: '#FFF',
                  },
                }}
              >
                Read API Docs
              </Button>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ 
              p: 5, 
              height: '100%', 
              backgroundColor: 'rgba(255, 255, 255, 0.02)', 
              borderRadius: '24px',
              border: '1px solid #1A1A1A',
              backgroundImage: 'none',
              transition: 'all 0.3s',
              '&:hover': { borderColor: '#10B981', backgroundColor: 'rgba(255, 255, 255, 0.03)' }
            }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#FFF', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Email sx={{ color: '#10B981' }} />
                Get in Touch
              </Typography>
              <Typography variant="body1" sx={{ color: '#999', mb: 4, lineHeight: 1.7 }}>
                Need help? Contact us.
              </Typography>
              <Box sx={{ mb: 4 }}>
                <Box sx={{ mb: 3 }}>
                   <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>Email Address</Typography>
                   <Typography variant="h6">
                     <Link href="mailto:info@darukaa.com" sx={{ color: '#FFF', textDecoration: 'none', '&:hover': { color: '#10B981' } }}>
                       info@darukaa.com
                     </Link>
                   </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                   <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>Official Website</Typography>
                   <Typography variant="h6">
                     <Link href="https://www.darukaa.earth" target="_blank" rel="noopener noreferrer" sx={{ color: '#FFF', textDecoration: 'none', '&:hover': { color: '#10B981' } }}>
                       www.darukaa.earth
                     </Link>
                   </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default HelpSupportPage;

